import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'next-themes';
import { supabase } from '@/integrations/supabase/client';
import { getGoogleClientId } from '@/lib/googleClientId';
import { Spinner } from '@/components/ui/spinner';
import { resolveOAuthErrorCopy } from '@/lib/oauthError';
import { postLoginRedirect } from '@/lib/postLoginRedirect';
import logoLight from '@/assets/logo-light.svg';
import logoDark from '@/assets/logo-dark.svg';

const Auth = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('auth');
  // Die OAuth-Fehlercopy liegt im Namespace `common`, weil sie sich Anmeldung,
  // Verbinden-Button, Reauth-Dialog und Wizard teilen (siehe src/lib/oauthError.ts).
  const { t: tCommon } = useTranslation('common');
  const { resolvedTheme } = useTheme();
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);

    // Open popup immediately to prevent browser blocking
    const popup = window.open(
      'about:blank',
      'google-oauth-auth',
      'width=500,height=600,scrollbars=yes,resizable=yes'
    );

    try {
      const redirectUri = `${window.location.origin}/auth/google/callback`;
      // Retry + localStorage-Fallback (Sentry DENO-T), gemeinsamer Helper aller OAuth-Flows.
      const clientId = await getGoogleClientId();
      const scope = [
        'https://www.googleapis.com/auth/content',
        'openid',
        'https://www.googleapis.com/auth/userinfo.email',
        'https://www.googleapis.com/auth/userinfo.profile'
      ].join(' ');

      // Use "auth:<nonce>" as state to signal auth mode in the callback
      const nonce = crypto.randomUUID();
      const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
      authUrl.searchParams.set('client_id', clientId);
      authUrl.searchParams.set('redirect_uri', redirectUri);
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('scope', scope);
      authUrl.searchParams.set('state', `auth:${nonce}`);
      authUrl.searchParams.set('access_type', 'offline');
      authUrl.searchParams.set('prompt', 'consent');
      authUrl.searchParams.set('include_granted_scopes', 'true');

      if (popup && !popup.closed) {
        popup.location.href = authUrl.toString();
      } else {
        // Full-tab redirect fallback
        window.location.href = authUrl.toString();
        return;
      }

      const handleMessage = async (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;

        if (event.data.type === 'GOOGLE_AUTH_SUCCESS') {
          popup?.close();
          window.removeEventListener('message', handleMessage);

          const { session } = event.data.data;
          if (session?.access_token && session?.refresh_token) {
            await supabase.auth.setSession({
              access_token: session.access_token,
              refresh_token: session.refresh_token,
            });
            // AuthContext will pick up the session change and navigate
          }
          setIsGoogleLoading(false);
        } else if (event.data.type === 'GOOGLE_OAUTH_ERROR') {
          popup?.close();
          window.removeEventListener('message', handleMessage);
          // Finding R3-3: bis hierhin kam nur "Google-Anmeldung fehlgeschlagen" plus die
          // Rohmeldung von supabase-js. Bei einem Google-Ausfall ist die Ueberschrift
          // zwar nicht falsch, aber sie legt nahe, dass etwas mit dem Konto nicht stimmt.
          // Mit dem Code steht da, was los ist und was zu tun ist.
          const copy = resolveOAuthErrorCopy(event.data.code, tCommon);
          toast.error(copy?.title ?? t('errors.signInFailed'), {
            description: copy?.description ?? (event.data.error || t('errors.genericError')),
          });
          setIsGoogleLoading(false);
        }
      };

      window.addEventListener('message', handleMessage);

      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', handleMessage);
          setIsGoogleLoading(false);
        }
      }, 500);

    } catch (error) {
      popup?.close();
      toast.error(t('errors.error'), {
        description: t('errors.signInError'),
      });
      setIsGoogleLoading(false);
    }
  };

  useEffect(() => {
    if (user && !loading) {
      // Zurueck an den Ort, der die Anmeldung ausgeloest hat. `replace`, damit
      // der Zurueck-Knopf nicht in die Anmeldeseite zurueckfaellt.
      navigate(postLoginRedirect(location.state), { replace: true });
    }
  }, [user, loading, navigate, location.state]);

  if (loading) {
    // R4: dieser Zweig traegt den B2B-Hinweis bewusst NICHT. Er ist ein
    // reiner Uebergangszustand (Session-Check vor dem Redirect oder vor dem
    // Formular), zeigt keinen Kauf- oder Anmeldeweg und ist in aller Regel
    // binnen Millisekunden wieder weg -- ein Rechtssatz, der hier aufblitzt
    // und sofort durch das Formular ersetzt wird, sichert nichts ab.
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-card rounded-[16px] shadow-xl p-8 sm:p-10">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img src={resolvedTheme === 'dark' ? logoDark : logoLight} alt="FeedOptimizer.AI" className="h-8" />
          </div>

          {/* Heading */}
          <h2 className="font-heading font-bold text-xl sm:text-2xl text-foreground text-center mb-2">
            {t('welcome')}
          </h2>

          {/* Subtitle */}
          <p className="text-muted-foreground text-sm text-center mb-8">
            {t('subtitle')}
          </p>

          {/* Google Sign-In Button */}
          <button
            onClick={handleGoogleSignIn}
            disabled={isGoogleLoading}
            className="w-full flex items-center justify-center gap-3 px-6 py-3.5 bg-white border border-border rounded-md font-medium text-[#1f1f1f] shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
          >
            {isGoogleLoading ? (
              <Spinner size="sm" />
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
            )}
            {t('signInGoogle')}
          </button>

          {/* Trust signals */}
          <div className="flex items-center justify-center gap-4 mt-6 text-xs text-muted-foreground">
            <span>{t('freeTier')}</span>
            <span className="w-1 h-1 rounded-full bg-muted-foreground/50" />
            <span>{t('noCreditCard')}</span>
          </div>

          {/* Privacy note */}
          <p className="text-[11px] text-muted-foreground/60 text-center mt-4 leading-relaxed">
            {t('privacyNote')}
          </p>
        </div>

        {/*
          R4 (Entlastung Rahmen): `/auth` liegt AUSSERHALB von `Layout`
          (siehe src/App.tsx, `<Route path="/auth" element={<Auth />} />`
          ohne `<Layout>`-Wrapper), Layout kann den Hinweis hier also nicht
          rendern. Diese Seite ist eine der drei Stellen aus
          `B2B_NOTICE_ROUTES` (src/lib/b2bNotice.ts) und traegt ihn deshalb
          selbst, im selben Stil wie zuvor in Layout.tsx.
        */}
        <p className="mt-6 text-center text-xs text-muted-foreground">
          {tCommon('b2bNotice')}
        </p>
      </div>
    </div>
  );
};

export default Auth;
